import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { request } from '@/api/request';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import { SortDirection, type DatasetRequest } from '@@/packages/shared/libs/search/types';

import type { Document, DocumentDatasetResponse } from '../types';
import { DOCUMENTS_QUERY, ORGANISATION_DOCUMENTS_QUERY } from './queryKeys';

export const fetchDocumentList = (payload: DatasetRequest) =>
  request<DocumentDatasetResponse>('POST', '/documents/list', payload);

export const fetchOrgDocuments = (orgID: string) =>
  request<Document[]>('GET', `/organisations/${orgID}/documents`);

export function useDocuments(params: MaybeRefOrGetter<DatasetRequest>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [DOCUMENTS_QUERY, toRef(params)] as const,
      async queryFn({ queryKey }) {
        const res = await fetchDocumentList(queryKey[1]);
        return res.data;
      },
    }),
  );

  const documents = computed(() => query.data.value?.documentsForList ?? []);
  const totalDocuments = computed(() => query.data.value?.totalDocuments ?? 0);
  const page = computed(() => query.data.value?.page ?? null);

  return { ...query, query, documents, totalDocuments, page };
}

/**
 * An opinionated document fetching function which is more likely to be in the
 * store already than passing manual args to `useDocuments`.
 */
export function useAllDocuments(options?: QueryOptions) {
  return useDocuments(
    {
      sort: [{ field: 'lastAttachmentOrModifiedDate', direction: SortDirection.Descending }],
      page: null,
    },
    options,
  );
}

export function useCreateDocument(
  options?: MutationOptions<
    Document,
    { name: string; uploadID: string; private: boolean; fileId?: string }
  >,
) {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    mergeMutationOptions(options, {
      async mutationFn(documentArguments) {
        const res = await request<Document>('POST', '/documents', documentArguments);
        return res.data;
      },
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY] });
      },
    }),
  );

  return mutation;
}

export function useDeleteDocument(options?: MutationOptions<never, Document>) {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    mergeMutationOptions(options, {
      async mutationFn(document) {
        const res = await request<never>('DELETE', `/documents/${document.id}`);
        return res.data;
      },
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY] });
      },
    }),
  );

  return mutation;
}

export function useRenameDocument(
  options?: MutationOptions<Document, { document: Document; newName: string }>,
) {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ document, newName }) {
        const res = await request<Document>('POST', `/documents/${document.id}/rename`, {
          name: newName,
        });
        return res.data;
      },
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY] });
      },
    }),
  );

  return mutation;
}

export function useReplaceDocument(
  options?: MutationOptions<Document, { document: Document; uploadID: string }>,
) {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ document, uploadID }) {
        const res = await request<Document>('POST', `/documents/${document.id}/replace`, {
          uploadID,
        });
        return res.data;
      },
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY] });
      },
    }),
  );

  return mutation;
}

export function useOrgDocuments(orgID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ORGANISATION_DOCUMENTS_QUERY, toRef(orgID)] as const,
      async queryFn({ queryKey }) {
        const res = await fetchOrgDocuments(queryKey[1]);
        return res.data;
      },
    }),
  );

  const documents = computed(() => query.data.value ?? []);

  return { ...query, query, documents };
}

export function useSetDocumentPrivateStatus(
  options?: MutationOptions<Document, { documentId: string; isPrivate: boolean }>,
) {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ documentId, isPrivate }) {
        const res = await request<Document>('POST', `/documents/${documentId}/private`, {
          private: isPrivate,
        });
        return res.data;
      },
      async onSuccess() {
        await queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY] });
      },
    }),
  );

  return mutation;
}
